import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from './Button.js';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  /** 危险操作（删除候选人 / 删除账号 / 确认成队）用红色确认按钮 */
  danger?: boolean;
  /** 需要用户手工输入该文字才能确认（删除账号等不可恢复操作） */
  requireText?: string;
  pending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * 二次确认弹窗（不使用 window.confirm）。
 * Esc / 点遮罩 = 取消；打开时锁定页面滚动并把焦点移入弹窗。
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmText = '确认',
  cancelText = '取消',
  danger = false,
  requireText,
  pending = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onCancel]);

  if (!open) return null;

  const mismatch = requireText !== undefined && typed.trim() !== requireText;

  return (
    <div
      className="modal-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={dialogRef}>
        <h2 className="modal-title">{title}</h2>
        {description !== undefined ? <div className="modal-body">{description}</div> : null}
        {requireText !== undefined ? (
          <div className="field">
            <label className="field-label" htmlFor="crossmatch-confirm-text">
              请输入「{requireText}」以确认
            </label>
            <input
              id="crossmatch-confirm-text"
              className={mismatch && typed.length > 0 ? 'input input-invalid' : 'input'}
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              placeholder={requireText}
              autoComplete="off"
            />
            {mismatch && typed.length > 0 ? <span className="field-error">输入不一致，无法确认</span> : null}
          </div>
        ) : null}
        <div className="modal-actions">
          <Button variant="ghost" onClick={onCancel} disabled={pending}>
            {cancelText}
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} loading={pending} disabled={mismatch}>
            {confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
}